import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronsUpDown, MessageSquare, Moon, MoreHorizontal, Sun } from 'lucide-react';
import { useNavbarActionSlot } from '../../../context/NavbarActionsContext';
import { useStreamChat } from '../../../context/StreamChatContext';
import { useTheme } from '../../../context/ThemeContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../ui/sheet';
import { cn } from '../../../lib/utils';
import { ACTION_ICON_MAP, buildCrumbs } from '../shell/AppHeader';

// Page actions inline in the head when there are this many or fewer; beyond
// that every action moves into the "More" sheet.
const MAX_INLINE_ACTIONS = 2;

// Action labels double as tooltips on desktop ("Share this exact view
// (copies a link…)"); the sheet row only needs the verb phrase.
const shortLabel = (label) => String(label || '').replace(/\s*\(.*\)\s*$/, '');

function HeadButton({ label, onClick, disabled, primary, children }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={cn('app-m-headbtn', primary && 'app-m-headbtn-primary')}
    >
      {children}
    </button>
  );
}

/**
 * Sticky head of the phone content card: the current page (a switcher over
 * its siblings), page actions, and a "More" sheet holding the rest of the
 * actions plus team chat and light/dark.
 */
export default function MobileCardHead({ trail }) {
  const navigate = useNavigate();
  const pageActions = useNavbarActionSlot();
  const { unreadMessageCount } = useStreamChat();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [moreOpen, setMoreOpen] = useState(false);

  const crumbs = buildCrumbs(trail).filter((c) => c.key !== 'group');
  const current = crumbs[crumbs.length - 1];
  const parent = crumbs.length > 1 ? crumbs[crumbs.length - 2] : null;
  const inline = pageActions.length <= MAX_INLINE_ACTIONS ? pageActions : [];
  const overflow = pageActions.length <= MAX_INLINE_ACTIONS ? [] : pageActions;

  const runFromSheet = (fn) => {
    setMoreOpen(false);
    // let the sheet close before the action opens its own dialog/print view
    setTimeout(() => fn?.(), 120);
  };

  return (
    <header className="app-m-head">
      <div className="app-m-crumb">
        {parent && (
          <>
            <span className="app-m-crumb-parent">{parent.title}</span>
            <span aria-hidden className="app-m-crumb-sep">/</span>
          </>
        )}
        {current && current.siblings ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="app-m-crumb-switch" aria-label={`${current.title}, switch page`}>
                <span className="truncate">{current.title}</span>
                <ChevronsUpDown className="size-3.5 shrink-0 opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-56">
              {current.siblings.map((s) => {
                const isCurrent = s.key === current.key;
                return (
                  <DropdownMenuItem
                    key={s.key}
                    onSelect={() => { if (!isCurrent) navigate(s.to); }}
                    className={cn('min-h-10', isCurrent && 'font-semibold')}
                  >
                    {s.title}
                    {isCurrent && <Check className="ml-auto size-4" />}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : current ? (
          <span className="app-m-crumb-title">{current.title}</span>
        ) : null}
      </div>

      <div className="app-m-head-actions">
        {inline.map((action) => {
          const Icon = ACTION_ICON_MAP[action.key];
          return (
            <HeadButton
              key={action.key}
              label={shortLabel(action.label)}
              onClick={action.onClick}
              disabled={action.disabled}
              primary={action.primary}
            >
              {Icon && <Icon className={cn('size-4', action.spinning && 'animate-spin')} />}
            </HeadButton>
          );
        })}
        <HeadButton label="More" onClick={() => setMoreOpen(true)}>
          <MoreHorizontal className="size-[18px]" />
          {unreadMessageCount > 0 && <span className="app-m-dot" aria-hidden />}
        </HeadButton>
      </div>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" hideClose className="app-m-more">
          <SheetHeader className="sr-only">
            <SheetTitle>More</SheetTitle>
            <SheetDescription>Page actions and app options</SheetDescription>
          </SheetHeader>
          <span aria-hidden className="app-m-grabber" />
          {overflow.length > 0 && (
            <div className="app-m-more-group">
              {overflow.map((action) => {
                const Icon = ACTION_ICON_MAP[action.key];
                return (
                  <button
                    key={action.key}
                    type="button"
                    className="app-m-more-row"
                    disabled={action.disabled}
                    onClick={() => runFromSheet(action.onClick)}
                  >
                    {Icon && <Icon className={cn('size-[18px]', action.spinning && 'animate-spin')} />}
                    <span>{shortLabel(action.label)}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="app-m-more-group">
            <button type="button" className="app-m-more-row" onClick={() => runFromSheet(() => navigate('/chat'))}>
              <MessageSquare className="size-[18px]" />
              <span>Team chat</span>
              {unreadMessageCount > 0 && (
                <span className="app-m-more-count">{unreadMessageCount > 99 ? '99+' : unreadMessageCount}</span>
              )}
            </button>
            <button type="button" className="app-m-more-row" onClick={() => { toggleDarkMode(); setMoreOpen(false); }}>
              {isDarkMode ? <Sun className="size-[18px]" /> : <Moon className="size-[18px]" />}
              <span>{isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
}
